export class Optional<T> {

    private static readonly EMPTY: Optional<never> = new Optional<never>( null );

    private constructor( private readonly value: T | null ) { }

    static empty<T>(): Optional<T> {
        return Optional.EMPTY;
    }

    static of<T>( value: T ): Optional<T> {
        return new Optional<T>( value );
    }

    static ofNullable<T>( value: T | null | undefined ): Optional<T> {
        if ( value == null ) {
            return Optional.empty();
        } else {
            return Optional.of( value );
        }
    }

    map<R>( mapper: ( t: T ) => R | null | undefined ): Optional<R> {
        if ( this.value == null ) {
            return Optional.empty();
        } else {
            return Optional.ofNullable( mapper( this.value ) );
        }
    }

    flatMap<R>( mapper: ( t: T ) => Optional<R> ): Optional<R> {
        if ( this.value == null ) {
            return Optional.empty();
        } else {
            return mapper( this.value );
        }
    }

    mapToPair<R>( mapper: ( t: T ) => R | null | undefined ): Optional<{ readonly t: T; readonly r: R }> {
        if ( this.value == null ) {
            return Optional.empty();
        } else {
            const tempResult = mapper( this.value );
            if ( tempResult == null ) {
                return Optional.empty();
            } else {
                return Optional.of( { t: this.value, r: tempResult } );
            }
        }
    }

    isEmpty(): boolean {
        return this.value == null;
    }

    isPresent(): boolean {
        return this.value != null;
    }

    ifPresent( f: ( t: T ) => void ): void {
        if ( this.value != null ) {
            f( this.value );
        }
    }

    ifPresentOrElse( f: ( t: T ) => void, r: () => void ) {
        if ( this.value == null ) {
            r();
        } else {
            f( this.value );
        }
    }

    filter( predicate: ( t: T ) => boolean ): Optional<T> {
        if ( this.value == null || !predicate( this.value ) ) {
            return Optional.empty();
        } else {
            return this;
        }
    }

    orElse( t: T ): T {
        return this.value ?? t;
    }

    orElseGet( f: () => T ): T {
        return this.value ?? f();
    }

    get(): T | null {
        return this.value;
    }
}
